/**
 * KaivaWordmark — the lockup, rendered as an inline SVG using the
 * canonical geometry from `design/kaiva-canvas/project/brand/`.
 *
 * Brand v1.3: the `i` is a dotless `ı` (Unicode U+0131) and the dot is
 * a separately-drawn Sienna circle (0.15× em diameter, snug-to-stem
 * 2 px gap above the cap-height line). The two-part construction lets
 * the dot read as a deliberate atomic unit at any size, independent
 * of font-rendering quirks.
 *
 * Geometry mirrors `kaiva-wordmark.svg` / `kaiva-wordmark-on-dark.svg`
 * exactly (viewBox 480×132). The `size` prop sets the wordmark's
 * visual cap-height; the SVG width derives from the natural aspect.
 */

type KaivaWordmarkTone = 'linen' | 'ink';

type KaivaWordmarkProps = {
  size?: number;
  tone?: KaivaWordmarkTone;
  className?: string;
  ariaLabel?: string;
};

const VIEWBOX_WIDTH = 480;
const VIEWBOX_HEIGHT = 132;
const ASPECT = VIEWBOX_WIDTH / VIEWBOX_HEIGHT;

export function KaivaWordmark({
  size = 16,
  tone = 'linen',
  className,
  ariaLabel = 'kaiva',
}: KaivaWordmarkProps) {
  // The wordmark text has font-size 120 inside a 132-tall viewBox.
  // To make `size` map to the visual wordmark height the consumer
  // expects, scale the SVG height to ~size × (132 / 120) so the
  // cap-height roughly equals `size`.
  const svgHeight = size * (VIEWBOX_HEIGHT / 120);
  const svgWidth = svgHeight * ASPECT;

  const textFill = tone === 'linen' ? '#F4F1E8' : '#0F172A';

  return (
    <svg
      width={svgWidth}
      height={svgHeight}
      viewBox={`0 0 ${VIEWBOX_WIDTH} ${VIEWBOX_HEIGHT}`}
      role="img"
      aria-label={ariaLabel}
      className={className}
      style={{ display: 'inline-block', flexShrink: 0 }}
    >
      <text
        x="240"
        y="116"
        textAnchor="middle"
        fontFamily="Geist, Inter, system-ui, -apple-system, sans-serif"
        fontSize="120"
        fontWeight="700"
        letterSpacing="-5.4"
        fill={textFill}
      >
        ka<tspan fill="#EC5800">ı</tspan>va
      </text>
      {/* Atomic unit — the captured node. cap-height ≈ 0.70 × em = 84
          (stem top at y=32); r=9 (diameter ≈ 0.15 × em); snug-to-stem
          gap of 2 px; cy = stem_top − gap − r = 32 − 2 − 9 = 21. */}
      <circle cx="242" cy="21" r="9" fill="#EC5800" />
    </svg>
  );
}
